// frontend/src/components/ResourcesTabContainer.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Updated:      2026-10-23 11:33 BST
//
// Description:
//   Resources tab composition container.
//
// Responsive pass (2026-09-16):
//   • Desktop (>1280px) keeps the existing 12-column layout with Create / Roles / Calendar in thirds.
//   • At 1280px and below, the three middle cards stack full-width.
//   • Resource List and Expenses remain full-width at every breakpoint.
//   • Replaces brittle inline-style attribute selectors with explicit container classes.
//   • No selection, refresh, create, role-linking, calendar, or expense behaviour changed.
//
// Change Log (BST):
// 2026-10-23 11:33  Added a full-width tenant-scoped Cost Centre Taxonomy manager
//                   between resource administration and expenses. Existing layout preserved.

import React, { useCallback, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

import ResourceFormWidget from "../widgets/ResourceFormWidget.jsx";
import ResourceListWidget from "../widgets/ResourceListWidget.jsx";
import ResourceRolesWidget from "../widgets/ResourceRolesWidget.jsx";
import ResourceExpensesWidget from "../widgets/ResourceExpensesWidget.jsx";
import OrgCalendarWidget from "../widgets/OrgCalendarWidget.jsx";
import CostCentreTaxonomyWidget from "../widgets/CostCentreTaxonomyWidget.jsx";

const ResourcesTabContainer = () => {
  const { theme } = useTheme();
  const [selectedResource, setSelectedResource] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [expensesRefreshKey, setExpensesRefreshKey] = useState(0);

  const card = useMemo(
    () => ({
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 10,
      padding: 14,
      minWidth: 0,
    }),
    []
  );

  const handleCreated = useCallback((res) => {
    setSelectedResource(res || null);
    setListRefreshKey((k) => k + 1);
  }, []);

  const handleEdited = useCallback(() => {
    setListRefreshKey((k) => k + 1);
    setExpensesRefreshKey((k) => k + 1);
  }, []);

  const onSelect = useCallback((res) => {
    setSelectedResource(res);
    setExpensesRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="resources-tab-container">
      {/* Top row: FULL-WIDTH resource list (no wrapper) */}
      <div className="resources-tab-grid">
        <div className="resources-tab-full">
          <ResourceListWidget
            key={`list-${listRefreshKey}`}
            onSelect={onSelect}
            onEdited={handleEdited}
          />
        </div>
      </div>

      {/* Middle row: thirds on desktop, stacked below 1280px */}
      <div className="resources-tab-grid">
        <div
          className="resources-tab-third resources-tab-third--first"
          style={card}
        >
          <h4
            style={{
              margin: 0,
              fontFamily: theme.font_header,
              overflowWrap: "anywhere",
            }}
          >
            Create Resource
          </h4>
          <div style={{ marginTop: 10, minWidth: 0 }}>
            <ResourceFormWidget onCreated={handleCreated} />
          </div>
        </div>

        <div
          className="resources-tab-third resources-tab-third--second"
          style={card}
        >
          <h4
            style={{
              margin: 0,
              fontFamily: theme.font_header,
              overflowWrap: "anywhere",
            }}
          >
            Roles & Linked User
          </h4>
          <div style={{ marginTop: 10, minWidth: 0 }}>
            <ResourceRolesWidget
              resource={selectedResource}
              onLinked={() => setListRefreshKey((k) => k + 1)}
              onRoleSaved={() => setListRefreshKey((k) => k + 1)}
            />
          </div>
        </div>

        <div
          className="resources-tab-third resources-tab-third--third"
          style={card}
        >
          <h4
            style={{
              margin: 0,
              fontFamily: theme.font_header,
              overflowWrap: "anywhere",
            }}
          >
            Organisational Calendar
          </h4>
          <div style={{ marginTop: 10, minWidth: 0 }}>
            <OrgCalendarWidget resourceId={selectedResource?.id || null} />
          </div>
        </div>
      </div>

      {/* Tenant-scoped taxonomy administration: not globally registered */}
      <div className="resources-tab-grid">
        <div className="resources-tab-full" style={card}>
          <CostCentreTaxonomyWidget />
        </div>
      </div>

      {/* Bottom row: FULL-WIDTH expenses — NO CARD, NO EXTRA HEADING */}
      <div className="resources-tab-grid">
        <div className="resources-tab-full">
          <ResourceExpensesWidget
            key={`exp-${expensesRefreshKey}`}
            selectedResourceId={selectedResource?.id || ""}
          />
        </div>
      </div>

      <style>{`
        .resources-tab-container {
          display: grid;
          gap: 14px;
          min-width: 0;
          width: 100%;
        }

        .resources-tab-grid {
          display: grid;
          gap: 14px;
          grid-template-columns: repeat(12, minmax(0, 1fr));
          min-width: 0;
          width: 100%;
        }

        .resources-tab-full {
          grid-column: 1 / -1;
          min-width: 0;
        }

        .resources-tab-third {
          min-width: 0;
        }

        .resources-tab-third--first {
          grid-column: 1 / span 4;
        }

        .resources-tab-third--second {
          grid-column: 5 / span 4;
        }

        .resources-tab-third--third {
          grid-column: 9 / span 4;
        }

        @media (max-width: 1280px) {
          .resources-tab-grid {
            grid-template-columns: minmax(0, 1fr);
          }

          .resources-tab-full,
          .resources-tab-third,
          .resources-tab-third--first,
          .resources-tab-third--second,
          .resources-tab-third--third {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 640px) {
          .resources-tab-container,
          .resources-tab-grid {
            gap: 10px;
          }

          .resources-tab-third {
            padding: 12px !important;
            border-radius: 8px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ResourcesTabContainer;
